import React, { useState, useEffect } from 'react';
import { WifiOff, CloudUpload, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { pendingCount } from '../utils/offlineQueue';

export const OfflineBanner = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [pending, setPending] = useState(0);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    const onOnline = () => setIsOnline(true);
    const onOffline = () => setIsOnline(false);
    const onQueueChange = (e) => setPending(e.detail?.count ?? 0);
    const onSyncStatus = (e) => {
      setSyncing(e.detail?.syncing || false);
      if (!e.detail?.syncing && e.detail?.synced > 0) {
        toast.success(`Synced ${e.detail.synced} saved ${e.detail.synced === 1 ? 'entry' : 'entries'}!`, { icon: <CheckCircle2 className="w-4 h-4" /> });
      }
      if (!e.detail?.syncing && e.detail?.dropped > 0) {
        toast.error(`${e.detail.dropped} saved ${e.detail.dropped === 1 ? 'entry' : 'entries'} couldn't be synced and ${e.detail.dropped === 1 ? 'was' : 'were'} removed`);
      }
    };
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    window.addEventListener('offline-queue-changed', onQueueChange);
    window.addEventListener('offline-sync-status', onSyncStatus);
    pendingCount().then(setPending);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
      window.removeEventListener('offline-queue-changed', onQueueChange);
      window.removeEventListener('offline-sync-status', onSyncStatus);
    };
  }, []);

  if (isOnline && pending === 0 && !syncing) return null;

  return (
    <div
      className={`sticky top-0 z-[60] px-4 py-2 text-sm font-semibold flex items-center justify-center gap-2 text-white ${!isOnline ? 'bg-orange-600' : 'bg-blue-600'}`}
      data-testid="offline-banner"
    >
      {!isOnline ? (
        <>
          <WifiOff className="w-4 h-4" />
          You're offline — entries will be saved on this device and synced when you're back online
          {pending > 0 && <span className="bg-white/20 rounded-full px-2 py-0.5 text-xs">{pending} waiting</span>}
        </>
      ) : (
        <>
          <CloudUpload className={`w-4 h-4 ${syncing ? 'animate-pulse' : ''}`} />
          {syncing ? `Syncing ${pending} saved ${pending === 1 ? 'entry' : 'entries'}...` : `${pending} saved ${pending === 1 ? 'entry' : 'entries'} waiting to sync`}
        </>
      )}
    </div>
  );
};
